/**
 * client/input/clinput.ts — client button state + stance/ADS/sprint/use logic -> usercmd button bits
 * (pure, DOM-free; time is passed in as ms).
 *
 * OWNER: INPUT agent.
 * Port of T5 client_mp/cl_input_mp.cpp (research/controller.md §6, MED):
 *  - kbutton_t (IN_KeyDown/IN_KeyUp, `wasPressed` latch consumed by CL_UpdateCmdButton): a tap between two
 *    cmd slots still reaches the next cmd [NET §7.2].
 *  - +stance (IN_Stance_Down/Up + CL_StanceButtonUpdate): tap = crouch<->stand, from prone a tap = crouch,
 *    hold cl_stanceHoldTime 300 ms = prone (or stand when the press started prone), sprinting + hold
 *    cl_dtpHoldTime 200 ms = dive to prone, sprinting + release earlier = crouch.
 *  - +gostand (IN_GoStandDown): stands when crouched/prone instead of jumping.
 *  - +speed_throw (hold ADS) / +toggleads_throw (IN_ToggleADS, val >= 0.1 with latch); the ADS bit is
 *    holdADS XOR usingAds (CL_KeyMove `kb[SPEED].active == !usingAds`); pressing hold-ADS clears the toggle
 *    (IN_SpeedDown -> IN_LeaveADS).
 *  - +breath_sprint: sprint bit latched (active || wasPressed) unless BACK is held (CL_KeyMove).
 *  - temp stance keys +prone / +movedown (hold) override the stance machine (CL_KeyMove).
 *  - togglecrouch / toggleprone (CL_ToggleStance), lowerstance / raisestance.
 *  - respawn / freeze -> stand + ADS toggle off (CG_Respawn, cg_predict PMF respawned).
 * Our contract mapping (shared/types.ts Button): CROUCH/PRONE are desired-stance LEVELS; dive-to-prone
 * = PRONE while sprinting (pmove decides), so CL_STANCE_DIVE_TO_PRONE sends PRONE.
 * +usereload: RELOAD while held (T5 sends BUTTON_USERELOAD as a level); USE once held >= USE_HOLD_MS
 * (g_useholdtime 250 ms console value, ESTIMATE [CTL §6]).
 */
import { DTP_HOLD_MS, STANCE_HOLD_MS, USE_HOLD_MS, VIEWHEIGHT_STAND } from '@shared/constants';
import { Button, PMF, type PlayerState } from '@shared/types';

/** kbutton slots (ours; T5 numbers in comments). */
export const KB = {
  FORWARD: 0, //    +forward          (T5 2)
  BACK: 1, //       +back             (T5 3)
  MOVELEFT: 2, //   +moveleft         (T5 6)
  MOVERIGHT: 3, //  +moveright        (T5 7)
  SPEED: 4, //      +speed (hold ADS) (T5 9)
  UP: 5, //         +moveup = jump    (T5 10)
  DOWN: 6, //       +movedown = hold crouch (T5 11, temp stance)
  GOSTAND: 7, //    +gostand          (T5 12)
  ATTACK: 8, //     (T5 17 -> bit 0)
  BREATH: 9, //     (T5 18)
  FRAG: 10, //      (T5 19)
  SMOKE: 11, //     (T5 20)
  MELEE: 12, //     (T5 21)
  ACTIVATE: 13, //  +activate (use)   (T5 22)
  RELOAD: 14, //    +reload           (T5 23)
  USERELOAD: 15, // +usereload        (T5 24)
  PRONE: 16, //     +prone (hold, temp stance) (T5 27)
  THROW: 17, //     +throw            (T5 29)
  SPRINT: 18, //    +sprint           (T5 30)
  WEAPNEXT: 19, //  +weapnext_inventory (cgame command in T5; a level bit in our contract)
  SCORES: 20, //    togglescores (held)
} as const;
export type KB = (typeof KB)[keyof typeof KB];
const KB_COUNT = 21;

export class KButton {
  /** Sources currently holding the button (keys / pad buttons), like kbutton_t.down[2]. */
  readonly down = new Set<string>();
  active = false;
  wasPressed = false;
  downtime = 0;
  val = 0;
}

/** cl->stance (T5 StanceState). */
export const ClStance = { STAND: 0, CROUCH: 1, PRONE: 2, DIVE_TO_PRONE: 3 } as const;
export type ClStance = (typeof ClStance)[keyof typeof ClStance];

/** What the input layer needs from the predicted player state. */
export interface StanceView {
  /** CG_IsSprinting: viewHeightCurrent >= 60 && SPRINTING. */
  sprinting: boolean;
  /** Predicted ps.pm_flags / ps.pm_time (sprint latch; omitted = unknown, the latch then only clears on its own rules). */
  pmFlags?: number;
  pmTime?: number;
}

/**
 * Buttons that, sent while a sprint click is still waiting to engage, cancel it (the player chose to do
 * something else). The PM_SprintStartInterferingButtons set minus JUMP (a click in the air re-engages, MOVPUB §2.5)
 * and minus the stance levels (CROUCH/PRONE are levels in our contract, sent every cmd while crouched). ESTIMATE.
 */
const SPRINT_LATCH_CANCEL = Button.ATTACK | Button.ADS | Button.MELEE | Button.FRAG | Button.TACTICAL | Button.RELOAD;

export function isSprinting(ps: Pick<PlayerState, 'viewHeightCurrent' | 'pm_flags'>): boolean {
  // T5 CG_IsSprinting: ps->viewHeightCurrent >= 60.0 && (pm_flags & PMF_SPRINTING)
  return ps.viewHeightCurrent >= VIEWHEIGHT_STAND && (ps.pm_flags & PMF.SPRINTING) !== 0;
}

export class ClientInputState {
  readonly kb: KButton[] = Array.from({ length: KB_COUNT }, () => new KButton());
  stance: ClStance = ClStance.STAND;
  stanceHeld = false;
  stancePosition: ClStance = ClStance.STAND;
  stanceTime = 0;
  /** Toggle-ADS state (cl->usingAds). */
  usingAds = false;
  private toggleAdsLatch = false;
  /**
   * Sprint click latch (console "click L3 once"): set on the +breath_sprint press edge, keeps the SPRINT bit
   * on so pmove re-engages the sprint in the air after a jump (Jump_Start clears sprintButtonUpRequired and
   * PM_SprintStartInterferingButtons allows JUMPING && pm_time == 0) [MOVPUB §2.5, §2.7 ESTIMATE "a click, and
   * the sprint stays on until the stick leaves the forward zone"]. Cleared when the sprint ends on the ground
   * (stick out of the forward zone, ADS, fire, out of time, stance...), when a pending click meets a stick that
   * is not pushed forward at all, on BACK, on ADS/fire/melee/offhand/reload while pending, and on key release-all.
   */
  sprintLatched = false;
  /** The latched click has engaged a sprint (PMF_SPRINTING seen while latched). */
  private sprintEngaged = false;
  /** ✕ was used to stand up: suppress the jump bit until it is released (T5: kb[UP] never went down). */
  goStandUsedForStance = false;

  // ---- kbutton_t ---------------------------------------------------------------------------
  /** IN_KeyDown. Returns true on the 0->1 transition. */
  keyDown(b: KB, src: string, now: number, val = 1): boolean {
    const k = this.kb[b];
    k.val = val;
    if (k.down.has(src)) return false;
    k.down.add(src);
    if (!k.active) {
      k.active = true;
      k.wasPressed = true;
      k.downtime = now;
      return true;
    }
    return false;
  }
  /** IN_KeyUp. Returns true on the 1->0 transition. */
  keyUp(b: KB, src: string): boolean {
    const k = this.kb[b];
    if (!k.down.delete(src)) return false;
    if (k.down.size === 0) {
      k.active = false;
      k.val = 0;
      return true;
    }
    return false;
  }
  /** Analog value update while held (triggers). */
  keyValue(b: KB, val: number): void { this.kb[b].val = val; }
  isActive(b: KB): boolean { return this.kb[b].active; }
  /** Release everything (blur / pointer-lock loss / pause): avoids stuck keys. */
  clearKeys(): void {
    for (const k of this.kb) { k.down.clear(); k.active = false; k.wasPressed = false; k.val = 0; }
    this.stanceHeld = false;
    this.toggleAdsLatch = false;
    this.goStandUsedForStance = false;
    this.sprintLatched = false;
    this.sprintEngaged = false;
  }
  /** Release every key held by sources whose name starts with prefix (e.g. a disconnected pad). */
  releaseSources(prefix: string): void {
    for (let i = 0; i < this.kb.length; i++) {
      for (const src of [...this.kb[i].down]) if (src.startsWith(prefix)) this.keyUp(i as KB, src);
    }
  }

  private tempStanceActive(): boolean {
    return this.kb[KB.PRONE].active || this.kb[KB.DOWN].active;
  }

  /** CL_SetStance: ignored while a temp stance key (+prone / +movedown) is held. */
  setStance(s: ClStance): void {
    if (!this.tempStanceActive()) this.stance = s;
  }

  // ---- +stance (gamepad crouch/prone button) ------------------------------------------------
  /** IN_Stance_Down. */
  stanceDown(now: number, v: StanceView): void {
    if (this.tempStanceActive()) return;
    if (v.sprinting) {
      this.stanceHeld = true;
      this.stanceTime = now;
    } else {
      this.stanceHeld = true;
      // after a dive the client stance is DIVE_TO_PRONE while the player lies prone: treat it as prone
      // (T5 resolves this in cgame; ESTIMATE)
      if (this.stance === ClStance.DIVE_TO_PRONE) this.stance = ClStance.PRONE;
      this.stancePosition = this.stance;
      this.stanceTime = now;
      if (this.stancePosition !== ClStance.CROUCH) this.stance = ClStance.CROUCH;
    }
  }
  /** IN_Stance_Up. */
  stanceUp(v: StanceView): void {
    if (this.tempStanceActive()) return;
    // sprinting + released before cl_dtpHoldTime = crouch [CTL §6]. Once the hold has triggered the dive
    // (stanceUpdate cleared stanceHeld and set DIVE_TO_PRONE) the release must not undo it, even if the
    // predicted ps still reads sprinting (dtp_startup_delay).
    if (v.sprinting && this.stanceHeld) {
      this.stance = ClStance.CROUCH;
      this.stanceHeld = false;
    }
    if (this.stanceHeld && this.stancePosition === ClStance.CROUCH) this.stance = ClStance.STAND;
    this.stanceHeld = false;
  }
  /** CL_StanceButtonUpdate: the hold timers (call before building each cmd). */
  stanceUpdate(now: number, v: StanceView): void {
    if (this.tempStanceActive() || !this.stanceHeld) return;
    const hold = v.sprinting ? DTP_HOLD_MS : STANCE_HOLD_MS; // cl_dtpHoldTime 200 / cl_stanceHoldTime 300
    if (now - this.stanceTime >= hold) {
      if (v.sprinting) this.stance = ClStance.DIVE_TO_PRONE;
      else if (this.stancePosition === ClStance.PRONE) this.stance = ClStance.STAND;
      else this.stance = ClStance.PRONE;
      this.stanceHeld = false;
    }
  }

  // ---- keyboard stance commands -------------------------------------------------------------
  /** CL_ToggleStance (togglecrouch / toggleprone); prone while sprinting = dive. */
  toggleStance(preferred: ClStance, v: StanceView): void {
    if (this.tempStanceActive()) return;
    if (v.sprinting && preferred === ClStance.PRONE) preferred = ClStance.DIVE_TO_PRONE;
    this.stance = preferred !== this.stance ? preferred : ClStance.STAND;
  }
  /** IN_LowerStance. */
  lowerStance(): void {
    if (this.tempStanceActive()) return;
    if (this.stance === ClStance.STAND) this.stance = ClStance.CROUCH;
    else if (this.stance === ClStance.CROUCH) this.stance = ClStance.PRONE;
  }
  /** IN_RaiseStance. */
  raiseStance(): void {
    if (this.tempStanceActive()) return;
    if (this.stance === ClStance.CROUCH) this.stance = ClStance.STAND;
    else if (this.stance === ClStance.PRONE || this.stance === ClStance.DIVE_TO_PRONE) this.stance = ClStance.CROUCH;
  }

  // ---- +gostand (✕ / Space) ------------------------------------------------------------------
  /** IN_GoStandDown: stand up when crouched/prone, else jump. */
  goStandDown(src: string, now: number): void {
    this.keyDown(KB.GOSTAND, src, now);
    if (this.stance !== ClStance.STAND) {
      this.setStance(ClStance.STAND);
      this.goStandUsedForStance = true;
    } else {
      this.keyDown(KB.UP, src, now);
    }
  }
  /** IN_GoStandUp. */
  goStandUp(src: string): void {
    this.keyUp(KB.GOSTAND, src);
    this.keyUp(KB.UP, src);
    if (!this.kb[KB.GOSTAND].active) this.goStandUsedForStance = false;
  }

  // ---- ADS -------------------------------------------------------------------------------------
  /** IN_Speed_Throw_Down (+speed_throw): hold ADS; also leaves toggle-ADS (IN_LeaveADS). */
  speedDown(src: string, now: number, val = 1): void {
    this.usingAds = false;
    this.keyDown(KB.SPEED, src, now, val);
    this.keyDown(KB.THROW, src, now, val);
  }
  speedUp(src: string): void {
    this.keyUp(KB.SPEED, src);
    this.keyUp(KB.THROW, src);
  }
  /**
   * IN_ToggleADS_Throw: toggles usingAds when the button value is >= 0.1, latched until it drops
   * below (call on press, on release (val 0) and on analog value changes while held).
   */
  toggleAdsValue(val: number): void {
    if (val >= 0.1) {
      if (!this.toggleAdsLatch) { this.usingAds = !this.usingAds; this.toggleAdsLatch = true; }
    } else {
      this.toggleAdsLatch = false;
    }
  }
  /** Current ADS request: holdADS XOR usingAds [CTL §6]. */
  adsRequested(): boolean {
    return this.kb[KB.SPEED].active !== this.usingAds;
  }

  /** CG_Respawn / map restart / PMF frozen: stance stand, ADS toggle off. */
  resetForSpawn(): void {
    this.stance = ClStance.STAND;
    this.stanceHeld = false;
    this.usingAds = false;
    this.sprintLatched = false;
    this.sprintEngaged = false;
  }

  /** The desired-stance bits this cmd would carry (no latch consumption). */
  stanceBits(): number {
    // CL_KeyMove: temp stance keys win
    if (this.tempStanceActive()) return this.kb[KB.PRONE].active ? Button.PRONE : Button.CROUCH;
    switch (this.stance) {
      case ClStance.CROUCH: return Button.CROUCH;
      case ClStance.PRONE:
      case ClStance.DIVE_TO_PRONE: return Button.PRONE;
      default: return 0;
    }
  }

  /** CL_UpdateCmdButton: active || wasPressed, then clear wasPressed. */
  private take(b: KB): boolean {
    const k = this.kb[b];
    const v = k.active || k.wasPressed;
    k.wasPressed = false;
    return v;
  }
  /** Peek without consuming. */
  peek(b: KB): boolean {
    const k = this.kb[b];
    return k.active || k.wasPressed;
  }

  /**
   * CL_CmdButtons + CL_KeyMove button part for one cmd slot. Consumes the wasPressed latches.
   * (Movement axes and the weapon request are added by the caller.)
   */
  buildButtons(now: number, v: StanceView, forwardmove = 127): number {
    this.stanceUpdate(now, v);
    let bits = this.stanceBits();
    if (this.take(KB.ATTACK)) bits |= Button.ATTACK;
    if (this.take(KB.BREATH)) bits |= Button.HOLD_BREATH;
    if (this.take(KB.FRAG)) bits |= Button.FRAG;
    if (this.take(KB.SMOKE)) bits |= Button.TACTICAL;
    if (this.take(KB.MELEE)) bits |= Button.MELEE;
    if (this.take(KB.ACTIVATE)) bits |= Button.USE;
    if (this.take(KB.RELOAD)) bits |= Button.RELOAD;
    const ur = this.kb[KB.USERELOAD];
    if (this.take(KB.USERELOAD)) bits |= Button.RELOAD;
    if (ur.active && now - ur.downtime >= USE_HOLD_MS) bits |= Button.USE;
    // jump: kb[UP] (gostand only presses it when already standing)
    if (this.take(KB.UP) && !this.goStandUsedForStance) bits |= Button.JUMP;
    if (this.kb[KB.GOSTAND].active && this.goStandUsedForStance) bits |= Button.GOSTAND;
    this.kb[KB.GOSTAND].wasPressed = false;
    // ADS = holdADS XOR toggle (not latched in T5)
    if (this.adsRequested()) bits |= Button.ADS;
    // sprint: latched unless BACK is held (CL_KeyMove), plus the click latch (see sprintLatched)
    if (this.updateSprintLatch(v, forwardmove, bits)) bits |= Button.SPRINT;
    if (this.take(KB.WEAPNEXT)) bits |= Button.SWITCH_WEAPON;
    if (this.kb[KB.SCORES].active) bits |= Button.SCOREBOARD;
    this.kb[KB.THROW].wasPressed = false;
    return bits;
  }

  /** Sprint bit for this cmd: kbutton (active || wasPressed) or the click latch. Consumes wasPressed. */
  private updateSprintLatch(v: StanceView, forwardmove: number, bits: number): boolean {
    const k = this.kb[KB.SPRINT];
    if (this.kb[KB.BACK].active) {
      k.wasPressed = false;
      this.sprintLatched = false;
      this.sprintEngaged = false;
      return false;
    }
    const pressed = k.wasPressed;
    if (pressed) { this.sprintLatched = true; this.sprintEngaged = false; }
    const held = this.take(KB.SPRINT);
    if (this.sprintLatched && v.pmFlags !== undefined) {
      const sprinting = (v.pmFlags & PMF.SPRINTING) !== 0;
      // airborne in a jump: PMF_JUMPING && pm_time == 0 (pm_time > 0 = landing slowdown) [PM §3 Jump_Start]
      const jumpAir = (v.pmFlags & PMF.JUMPING) !== 0 && !v.pmTime;
      if (sprinting) this.sprintEngaged = true;
      else if (this.sprintEngaged && !jumpAir) { this.sprintLatched = false; this.sprintEngaged = false; }
    }
    if (this.sprintLatched && !this.sprintEngaged && !pressed) {
      // a click still waiting to engage: dropped when the stick is not pushed forward at all, or when the player
      // ADSes / fires / throws / melees / reloads instead (ESTIMATE)
      if (forwardmove <= 0 || (bits & SPRINT_LATCH_CANCEL) !== 0) this.sprintLatched = false;
    }
    return held || this.sprintLatched;
  }

  /** Keyboard movement (CL_KeyMove, digital keys -> +-127). Returns [forwardmove, rightmove]. */
  keyMove(out: [number, number]): [number, number] {
    out[0] = (this.kb[KB.FORWARD].active ? 127 : 0) - (this.kb[KB.BACK].active ? 127 : 0);
    out[1] = (this.kb[KB.MOVERIGHT].active ? 127 : 0) - (this.kb[KB.MOVELEFT].active ? 127 : 0);
    return out;
  }
}
